import type {
  AccessCatalogItemPublic,
  AccessRecordPublic,
  ApplicationPublic,
  UserPublic,
} from '@accounts/shared';

import { STR } from '../../../../i18n';
import { monoStyle } from '../../../../ui/styles';
import { itemLabel, passwordHandlingLabel, recordStatusLabel } from '../accessFormat';
import type { AppStatusKey } from '../appStatus';

import { MATRIX_CELL_CLASS } from './model';

/** The word's colour class per open status — always alongside the word itself. */
const OPEN_TONE = {
  needs_access: 'tx-warn-strong',
  requested: 'tx-subtle',
  remove_access: 'tx-danger-strong',
  removed: 'tx-subtle',
} as const;

/**
 * One person × row. Words, not tags (the demo's rule): the login when active, otherwise the
 * tracker's own word (NEEDS / REQUESTED / REMOVE) in its colour — never colour alone. A
 * shared login is underlined, and the title says who else uses it and how the password is
 * handled. A filled cell opens the record and its history; an empty one opens "No access
 * recorded", whose Add access… opens the add form.
 */
export function MatrixCell({
  person,
  item,
  record,
  quiet,
  onOpen,
  onEmpty,
}: {
  person: UserPublic;
  item: AccessCatalogItemPublic;
  record: AccessRecordPublic | undefined;
  /** "Only what needs work": an active cell stays readable but steps back. */
  quiet: boolean;
  onOpen: (record: AccessRecordPublic) => void;
  /** An empty cell: opens "No access recorded", never the add form itself. */
  onEmpty: (userId: string, catalogItemId: string) => void;
}) {
  const rowName = itemLabel(item.path, item.name);

  if (record === undefined) {
    const title = STR.access.matrix.cellEmptyTitle(person.name, rowName);
    return (
      <button
        type="button"
        className={MATRIX_CELL_CLASS}
        title={title}
        aria-label={title}
        onClick={() => {
          onEmpty(person.id, item.id);
        }}
        style={cellStyle}
      >
        <span className="tx-faint">{STR.access.matrix.cellEmpty}</span>
      </button>
    );
  }

  const shared = record.sharedWith.length;
  const title = STR.access.matrix.cellTitle(
    person.name,
    rowName,
    recordStatusLabel(record.status),
    record.login ?? STR.accessCommon.noLogin,
    shared,
    passwordHandlingLabel(record.passwordHandling),
  );

  return (
    <button
      type="button"
      className={MATRIX_CELL_CLASS}
      title={title}
      aria-label={title}
      onClick={() => {
        onOpen(record);
      }}
      style={{ ...cellStyle, color: 'var(--text)', opacity: quiet && record.status === 'active' ? 0.4 : 1 }}
    >
      {record.status === 'active' ? (
        <span
          style={{
            ...monoStyle,
            ...(shared > 0
              ? { textDecoration: 'underline dotted', textUnderlineOffset: 3 }
              : {}),
          }}
        >
          {record.login ?? STR.common.dash}
        </span>
      ) : (
        <span
          className={OPEN_TONE[record.status]}
          style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '.03em' }}
        >
          {STR.accessCommon.recordCell[record.status]}
        </span>
      )}
    </button>
  );
}

/* No inline background: the resting `transparent` lives in `.mx-cell` (components.css), so
   `.mx-cell:hover` can win. An inline background beats every stylesheet rule. */
const cellStyle = {
  width: '100%',
  minWidth: 92,
  maxWidth: 140,
  height: 30,
  padding: '0 6px',
  border: 'none',
  textAlign: 'left',
  fontSize: 10.5,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  cursor: 'pointer',
} as const;

const APP_TONE: Record<AppStatusKey, string> = {
  granted: 'tx-success-strong',
  requested: 'tx-subtle',
  revoked: 'tx-faint',
  none: 'tx-faint',
};

/**
 * One person × suite app (FINAL demo): Granted, Asked, or a dot. A word, not a control — the
 * grant itself is made and revoked on Grants and the person page, and an ask is decided on
 * the Queue.
 */
export function AppMatrixCell({
  person,
  application,
  status,
}: {
  person: UserPublic;
  application: ApplicationPublic;
  status: AppStatusKey;
}) {
  const title = STR.access.matrix.appCellTitle(
    person.name,
    application.name,
    STR.access.matrix.appStatusWord[status],
  );
  return (
    <span
      className={MATRIX_CELL_CLASS}
      title={title}
      aria-label={title}
      style={{ ...cellStyle, display: 'flex', alignItems: 'center', minWidth: 76, cursor: 'default' }}
    >
      <span
        className={APP_TONE[status]}
        style={
          status === 'requested'
            ? { fontWeight: 700, fontSize: 10.5 }
            : { fontWeight: status === 'granted' ? 650 : 400 }
        }
      >
        {STR.access.matrix.appCell[status]}
      </span>
    </span>
  );
}
